import React, {useEffect} from 'react';
import {Input, FormControl} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import EventIcon from '@material-ui/icons/Event';
import {useDebouncedCallback} from 'use-debounce';
import wrap from 'wraptile';

import PlutoSelect, {PlutoSelectChange} from '@pluto/select';
import PlutoAutocomplete, {HandleAutocompleteChange} from '@pluto/autocomplete';
import {UserOption, UsersState, UserStatusEnum} from '@pluto/users/UsersTypes';
import DateRangePicker, {DateRangeState} from '@pluto/daterange-picker';

import {QCSpecificFilters, QCFiltersConnected} from './QCFiltersType';
import {
    HandleChangeSelectType,
    HandleChangeInputType,
    onDatePickerChange,
} from '../QCManager/QCManagerTypes';
import {UnassignedUser} from './constants';
import './QCFilters.scss';
import {curry} from 'ramda';

export interface QCFiltersContainerProps extends QCFiltersConnected {
    title: string;
    filters: QCSpecificFilters;
    handleStatusChange: PlutoSelectChange;
    handleAssigneeChange?: HandleAutocompleteChange;
    handleContentPartnersChange: HandleAutocompleteChange;
    handleContentTypesChange: HandleAutocompleteChange;
    handleTypeChange: HandleChangeSelectType;
    handleTitleChange: HandleChangeInputType;
    onDueDateRangeSelect: onDatePickerChange;
    onFinishDateRangeSelect: onDatePickerChange;
    dueDates: DateRangeState;
    finishDates: DateRangeState;
}

interface GetAssigneeFilter {
    handleAssigneeChange?: HandleAutocompleteChange;
    users: UsersState;
    assignee?: string[];
    onDebouncedInputChange: (value: any) => void;
}

export const getFilteredUsers = (user: UserOption) => {
    return user.status === 'Active' || user.status === 'Deactivated';
};

export const mapUsers = (user: UserOption) => ({
    value: user.id.toString(),
    label: user.name || user.email,
});

export const getAssigneeFilter = (handlers: GetAssigneeFilter) => {
    const {assignee, handleAssigneeChange, users, onDebouncedInputChange} = handlers;

    if (!handleAssigneeChange || !assignee) {
        return null;
    }

    const filteredOptions = users.rows.filter(getFilteredUsers);
    const options = filteredOptions.map(mapUsers);

    options.push(UnassignedUser);

    return (
        <FormControl className="qc-filter qc-filter__assignee">
            <PlutoAutocomplete
                onInputChangeCallback={onDebouncedInputChange}
                placeholder="Assignee"
                values={assignee}
                onChange={handleAssigneeChange}
                options={options}
                isLoading={users.isLoading}
            />
        </FormControl>
    );
};

export const handleMount = wrap((componentProps: QCFiltersConnected) => {
    const {
        contentPartners,
        setContentPartners,
        setUsers,
        qcJobsStatuses,
        setQCStatuses,
    } = componentProps;

    setUsers();

    if (contentPartners.isLoading) setContentPartners();

    if (qcJobsStatuses.isLoading) setQCStatuses();
});

export const handleDebounceCallback = curry((setUsers, name) => {
    setUsers({
        status: [UserStatusEnum.Active],
        name,
        limit: 20,
        offset: 0,
    });
});

export const handleDebounce = curry(({debouncedFunction}, newValue) => {
    debouncedFunction(newValue);
});

export default function QCFiltersContainer(props: QCFiltersContainerProps) {
    const {
        title,
        filters,
        handleStatusChange,
        handleAssigneeChange,
        handleContentPartnersChange,
        handleContentTypesChange,
        handleTitleChange,
        dueDates,
        finishDates,
        onDueDateRangeSelect,
        onFinishDateRangeSelect,
        contentPartners,
        setContentPartners,
        contentTypes,
        users,
        setUsers,
        qcJobsStatuses,
        setQCStatuses,
    } = props;

    const debounceCallback = handleDebounceCallback(setUsers);
    const [debouncedFunction] = useDebouncedCallback(debounceCallback, 900);
    const onDebouncedInputChange = handleDebounce({
        debouncedFunction,
    });

    const assigneeFilter = getAssigneeFilter({
        assignee: filters.qcJobAssigneeId,
        handleAssigneeChange,
        users,
        onDebouncedInputChange,
    });

    const onMount = handleMount({
        contentPartners,
        setContentPartners,
        users,
        setUsers,
        qcJobsStatuses,
        setQCStatuses,
    });

    useEffect(onMount, []);

    return (
        <div className="qc-filters-container">
            <div className="qc-filter qc-filter__search">
                <Input
                    autoComplete="off"
                    id="qc-filter__input"
                    value={title}
                    placeholder="Filter by title or ID"
                    onChange={handleTitleChange}
                />
                <SearchIcon className="qc-filter__search_icon" />
            </div>
            <div className="qc-filter qc-filter__due-date">
                <DateRangePicker
                    dates={dueDates}
                    onDatesSelect={onDueDateRangeSelect}
                    placeholder="Due date"
                />
                <EventIcon className="qc-filter__due-date_icon" />
            </div>
            <div className="qc-filter qc-filter__finish-date">
                <DateRangePicker
                    dates={finishDates}
                    onDatesSelect={onFinishDateRangeSelect}
                    placeholder="Finished date"
                />
                <EventIcon className="qc-filter__finish-date_icon" />
            </div>
            <div className="qc-filter qc-filter__content-partner">
                <PlutoAutocomplete
                    placeholder="Content partner"
                    values={filters.contentPartnerId}
                    onChange={handleContentPartnersChange}
                    options={contentPartners.data.rows}
                    isLoading={contentPartners.isLoading}
                />
            </div>
            {assigneeFilter}
            <div className="qc-filter qc-filter__content-type">
                <PlutoAutocomplete
                    placeholder="Content type"
                    values={filters.contentItemType}
                    onChange={handleContentTypesChange}
                    options={contentTypes.data}
                    isLoading={contentTypes.isLoading}
                />
            </div>
            <FormControl className="qc-filter qc-filter__job-status">
                <PlutoSelect
                    filterKey="qcJobStatus"
                    filters={filters}
                    placeholder="Status"
                    value={filters.qcJobStatus}
                    onChange={handleStatusChange}
                    options={qcJobsStatuses.statuses}
                    isLoading={qcJobsStatuses.isLoading}
                />
            </FormControl>
        </div>
    );
}
